import { type MessageDescriptor, type PrimitiveType } from "react-intl";

export type IntlValues = Record<string, PrimitiveType>;

export type MessagesMap = Record<string, MessageDescriptor>;

type FormatFn = (descriptor: MessageDescriptor, values?: IntlValues) => string;

const defaultFormat: FormatFn = (descriptor) =>
  String(descriptor.defaultMessage ?? "");

/**
 * Creates a message translator bound to a messages map.
 *
 * In static mode (default, no formatFn), resolves to `defaultMessage`.
 * Use this in Zod schemas, constants, and any non-React context where
 * hooks are unavailable.
 *
 * In hook mode, pass `intl.formatMessage` to enable runtime locale switching.
 * {@link useIntlMessages} wraps this for React component use.
 *
 * @param messages - Object of react-intl MessageDescriptors, typically from `defineMessages`.
 * @param formatFn - Optional format function. Defaults to returning `defaultMessage`.
 * @returns A translator `t(key | descriptor, values?)` → string.
 *
 * @example Static usage in a Zod schema
 * ```ts
 * const t = createTranslator(schemaMessages);
 * basicValidator(true, t("required"));
 * ```
 *
 * @example Hook usage (via useIntlMessages in a component)
 * ```ts
 * const t = useIntlMessages(schemaMessages);
 * t("required"); // locale-resolved string
 * ```
 */
export function createTranslator<T extends MessagesMap>(
  messages: T,
  formatFn: FormatFn = defaultFormat,
): (key: keyof T | MessageDescriptor, values?: IntlValues) => string {
  return (keyOrDescriptor, values) => {
    const descriptor =
      typeof keyOrDescriptor === "object"
        ? keyOrDescriptor
        : messages[keyOrDescriptor];

    return formatFn(descriptor, values);
  };
}
